/* eslint-disable import/prefer-default-export */
import { ValuesCache } from '@leverege/value-cache'
import { DateTime, Interval } from 'luxon'
import Constants from './Constants'

const {
  LEAD_TIME_MAX_MS,
  ONE_HOUR,
} = Constants

const cache = new ValuesCache()

export const getStationOccupancyItems = ( hItems, items ) => {
  return cache.calc(
    'items',
    () => {
      let minTime, maxTime

      const bins = new Map()
      if ( hItems == null ) {
        return { bins }
      }

      items.forEach( ( item ) => {
        bins.set( item.id, { deviceId : item.id, samples : [], obj : item } )
      } )

      for ( let n = 0; n < hItems.length; n++ ) {
        const h = hItems[n]

        const deviceId = h?.data?.featureDevice?.deviceId

        if ( !deviceId ) {
          continue
        }

        const bin = bins.get( deviceId )
        if ( bin == null ) {
          const obj = items.find( item => item.id === deviceId )
          bins.set( deviceId, { deviceId, samples : [ h ], obj } )
        } else {
          bin.samples.push( h )
        }
      }

      bins.forEach( ( value, key, map ) => {
        if ( !value?.obj?.data ) {
          return
        }

        const { samples } = value
        const events = []
        let dEvent = null
        let lastDEvent = null
        let lastTime = 0
        let totalLeadTime = 0
        let eventsWithLeadTime = 0
        // let sCar, sJack, sLift
        for ( let n = 0; n < samples.length; n++ ) {
          if ( !samples[n] ) continue
          const { receivedTime : rTime, data = {} } = samples[n]
          const { analyticsType } = data
          if ( analyticsType !== 'occupancy' ) {
            continue
          }
          const { time } = data
          const receivedTime = new Date( time || rTime ).getTime()
          lastTime = receivedTime

          const dog = data?.data?.counts?.dog

          if ( dog != null ) {
            if ( dEvent == null && dog > 0 ) {
              dEvent = {
                type : 'dogInStation',
                startTime : receivedTime,
                startData : data,
                obj : value.obj,
              }
              dEvent.id = samples[n].id

              minTime =
                minTime == null || minTime > receivedTime ?
                  receivedTime :
                  minTime
              maxTime =
                maxTime == null || maxTime < receivedTime ?
                  receivedTime :
                  maxTime

              events.push( dEvent )
            } else if ( dEvent && dog === 0 ) {
              dEvent.stopTime = receivedTime

              const duration = dEvent.stopTime - dEvent.startTime

              dEvent.duration = duration
              dEvent.stopData = data
              dEvent.id += `-${samples[n].id}`

              if ( lastDEvent ) {
                const leadTime = dEvent.startTime - lastDEvent.stopTime

                if ( leadTime < LEAD_TIME_MAX_MS ) {
                  totalLeadTime += leadTime
                  eventsWithLeadTime += 1
                  dEvent.leadTime = leadTime
                  dEvent.leadTimeAverage = totalLeadTime / eventsWithLeadTime
                }
              }

              lastDEvent = dEvent

              dEvent = null

              minTime =
                minTime == null || minTime > receivedTime ?
                  receivedTime :
                  minTime
              maxTime =
                maxTime == null || maxTime < receivedTime ?
                  receivedTime :
                  maxTime
            }
          }
        }
        if ( dEvent ) {
          dEvent.stopTime = Date.now() // lastTime
          dEvent.duration = dEvent.stopTime - dEvent.startTime
          dEvent.id += '-open'
          maxTime = maxTime == null || maxTime < lastTime ? lastTime : maxTime

          if ( lastDEvent ) {
            const leadTime = dEvent.startTime - lastDEvent.stopTime

            if ( leadTime < LEAD_TIME_MAX_MS ) {
              totalLeadTime += leadTime
              eventsWithLeadTime += 1
              dEvent.leadTime = leadTime
              dEvent.leadTimeAverage = totalLeadTime / eventsWithLeadTime
            }
          }

          lastDEvent = null
        }

        value.events = events
      } )

      return { minTime, maxTime, bins }
    },
    hItems,
    items,
  )
}

export const getUtilizationStats = (
  data,
  businessDays,
  type,
  selectedStation,
  timezone,
) => {
  let stationData

  if ( selectedStation ) {
    stationData = [ data.bins.get( selectedStation ) ]
  } else {
    stationData = Array.from( data.bins.values() )
  }
  const stats = stationData.reduce(
    ( prev, curr ) => {
      if ( !curr?.events ) return prev
      let lastEvent = null
      curr.events.forEach( ( event, index ) => {
        if ( event.type === type ) {
          const prevEvent = curr.events[index - 1]
          const eventStartDateTime = DateTime.fromMillis( event.startTime )
          const eventEndDateTime = DateTime.fromMillis( event.stopTime )

          if ( eventStartDateTime.day !== eventEndDateTime.day ) {
            return
          }

          if ( prevEvent ) {
            const prevEventEndDateTime = DateTime.fromMillis( prevEvent.stopTime )

            if ( eventStartDateTime.day === prevEventEndDateTime.day ) {
              if ( event.leadTime ) {
                prev.eventsWithLeadTime += 1
                prev.totalLeadTime += event.leadTime
              }
            }
          }

          prev.services.push( event )
          prev.totalOccupied += event.duration

          if ( lastEvent == null ) {
            lastEvent = event
          } else {
            const lastEventDateTime = DateTime.fromMillis( lastEvent.stopTime, {
              zone : timezone,
            } )

            if ( lastEventDateTime.day !== eventEndDateTime.day ) {
              lastEvent = event
              return
            }

            prev.totalUnoccupied += event.startTime - lastEvent.stopTime

            lastEvent = event
          }
        }
      } )

      return prev
    },
    {
      services : [],
      totalOccupied : 0,
      totalUnoccupied : 0,
      totalLeadTime : 0,
      eventsWithLeadTime : 0,
    },
  )

  let totalBusinessHours = 0

  if ( businessDays.length > 0 ) {
    const now = Date.now()
    businessDays.forEach( ( [ startTS, endTS ] ) => {
      totalBusinessHours += Interval.fromDateTimes(
        DateTime.fromMillis( startTS > now ? now : startTS ),
        DateTime.fromMillis( endTS > now ? now : endTS ),
      ).length( 'milliseconds' )
    } )
  }

  const totalUnoccupied = stats.totalUnoccupied // totalBusinessHours - stats.totalOccupied

  return {
    ...stats,
    averageServiceLength : stats.totalOccupied / ( stats.services.length || 1 ),
    totalBusinessHours,
    totalUnoccupied,
    percentOccupied : Math.round(
      ( stats.totalOccupied / ( stats.totalOccupied + totalUnoccupied ) ) * 100,
    ),
    percentUnoccupied : Math.round(
      ( totalUnoccupied / ( stats.totalOccupied + totalUnoccupied ) ) * 100,
    ),
    averageOccupied : stats.totalOccupied / ( businessDays.length || 1 ),
    averageUnoccupied : totalUnoccupied / ( businessDays.length || 1 ),
  }
}

export const getRowsByType = ( rows, type ) => {
  return cache.calc(
    `rows-${type}`,
    ( rows, type ) => {
      return rows.map( ( row ) => {
        if ( !row ) return []
        return row.filter( ( item ) => {
          return item.type === type
        } )
      } )
    },
    rows,
    type,
  )
}

export function isDuringBusinessHours( data, businessDays ) {
  let { startTime, stopTime } = data
  const { type, id } = data

  if ( type === 'carOnLift' ) {
    const [ startId, endId ] = id.split( '-' )

    startTime -= 30 + 1000 * offsetSeconds( 60, startId )
    stopTime += 30 + 1000 * offsetSeconds( 60, endId )
  }

  return businessDays.some( ( day ) => {
    const [ start, end ] = day

    return startTime >= start - ONE_HOUR && stopTime <= end + ONE_HOUR
  } )
}

function hashCode( str ) {
  let hash = 0
  if ( str == null || str.length === 0 ) {
    return hash
  }
  for ( let i = 0; i < str.length; i++ ) {
    const chr = str.charCodeAt( i )
    hash = ( hash << 5 ) - hash + chr
    hash |= 0 // Convert to 32bit integer
  }
  return Math.abs( hash ) // make it positive
}

export function offsetSeconds( number, key ) {
  return hashCode( key ) % number
}
